// Tolerant text matching for the catalog search - the two fallback stages behind the exact search:
//
//   1. exact search (engine.scoreNormalized)        - unchanged, always first
//   2. token search (compactForms / queryWords)     - "stahl beton" finds "Stahlbeton"
//   3. "Meinten Sie ...?" (FuzzyVocabulary below)   - "betonn" suggests "beton"
//
// Pure and dependency-free (no engine import) so engine.ts can build on it. Everything here takes
// ALREADY NORMALIZED text (lowercase, no diacritics - see engine.normalize).

const NON_ALNUM = /[^\p{L}\p{N}]+/gu;

/** The non-empty words of a normalized text. */
export const splitWords = (text: string): string[] => text.split(NON_ALNUM).filter(Boolean);

/** Spacing, punctuation and ß stop mattering: "stahl-beton" and "stahl beton" both become "stahlbeton". */
export const squash = (normalized: string): string => normalized.replace(NON_ALNUM, '').replace(/ß/g, 'ss');

/**
 * German transliteration folded onto the diacritic-free form normalize() produces: ae/oe/ue become
 * a/o/u (normalize already turned ä into a, so "traeger" and "träger" meet at "trager").
 *
 * Strictly per WORD: across a word boundary the same two letters are no umlaut - the a|e in
 * "Doka-Expressanker" must not become an "ä". That is why a text is never folded after its
 * separators were removed (see compactForms).
 */
export const foldWord = (word: string): string =>
    squash(word).replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u');

const FORM_SEPARATOR = '\u0000';

// The letter pairs foldWord rewrites.
const TRANSLITERATION = /ae|oe|ue/;

/**
 * The searchable forms of a text, kept in ONE string: its squashed form and, only when folding
 * changes something, its folded form behind a separator (most texts need just the first, which
 * keeps the cache small). A query word then matches if either of ITS two forms occurs - see hasWord:
 *
 *   text "Doka-Expressanker"  squashed "dokaexpressanker"   (folded is the same, stored once)
 *   query "doka expressanker" words "doka", "expressanker"  -> both found
 *   query "dokaexpressanker"  one word, plain form          -> found (its folded form would not be)
 *   query "traeger"           folded form "trager"          -> finds "Träger" (normalized: "trager")
 */
export const compactForms = (normalized: string): string => {
    const squashed = squash(normalized);
    // No ae/oe/ue anywhere, not even across a word boundary: there is nothing to fold, and the
    // per-word pass below (the costly part, run once per field of every article) can be skipped.
    if (!TRANSLITERATION.test(squashed)) {
        return squashed;
    }
    const folded = splitWords(normalized).map(foldWord).join('');
    return squashed === folded ? squashed : squashed + FORM_SEPARATOR + folded;
};

/** The squashed form alone - what article-number style prefix/exact fields are compared on. */
export const squashedForm = (forms: string): string => {
    const at = forms.indexOf(FORM_SEPARATOR);
    return at < 0 ? forms : forms.slice(0, at);
};

/** One word of a query in both of its comparable forms (`folded` equals `plain` if folding is moot). */
export interface QueryWord {
    plain: string;
    folded: string;
}

/**
 * The words of a normalized query. Words of one letter are dropped - every text contains them, they
 * would only widen the match - and so is a folded form that shrank to one letter ("ue" -> "u").
 */
export const queryWords = (normalizedQuery: string): QueryWord[] =>
    splitWords(normalizedQuery)
        .map((word) => {
            const plain = squash(word);
            const folded = foldWord(word);
            return { plain, folded: folded.length >= 2 ? folded : plain };
        })
        .filter((word) => word.plain.length >= 2);

/** Whether a text (as compactForms stored it) contains the query word in either of its forms. */
export const hasWord = (forms: string, word: QueryWord): boolean =>
    forms.includes(word.plain) || (word.folded !== word.plain && forms.includes(word.folded));

// ---- typo-tolerant vocabulary (SymSpell-style) ---------------------------------------------------
//
// Fuzzy-matching a query against every item would scan the whole catalog. Instead the DISTINCT
// WORDS of the searchable fields are indexed once (a few ten thousand, however big the catalog),
// and a typo is resolved against those. SymSpell's trick: precompute, per word, every variant with
// up to 2 letters deleted, in a Map. A query then looks up its own deletion variants and gets the
// few candidate words directly - no scan - and only those are verified with a real edit distance.
//
// Two memory limiters, both from SymSpell: deletions are only generated for the first
// PREFIX_LENGTH letters (the full distance is verified afterwards anyway), and short words get
// fewer deletions because they tolerate fewer typos (see typosAllowed).

const PREFIX_LENGTH = 7;
const MIN_WORD_LENGTH = 4;
const MAX_WORD_LENGTH = 32;
const SKIPPED = -1;

/** How many typos a word of this length tolerates: none below 4 letters (too ambiguous - "tor"
 * vs "rohr"), one up to 7, two from 8 on. Same staging search engines like Algolia use. */
const typosAllowed = (length: number): number => (length < MIN_WORD_LENGTH ? 0 : length < 8 ? 1 : 2);

/** Distinct variants of `word` with up to `depth` letters removed, the word itself included. */
const deletions = (word: string, depth: number): Set<string> => {
    const out = new Set<string>([word]);
    let frontier = [word];
    for (let d = 0; d < depth; d++) {
        const next: string[] = [];
        for (const w of frontier) {
            if (w.length <= 1) {
                continue;
            }
            for (let i = 0; i < w.length; i++) {
                const variant = w.slice(0, i) + w.slice(i + 1);
                if (!out.has(variant)) {
                    out.add(variant);
                    next.push(variant);
                }
            }
        }
        frontier = next;
    }
    return out;
};

/** Optimal-string-alignment distance (Levenshtein + adjacent transposition: "beotn" is ONE typo
 * from "beton"), giving up as soon as it must exceed `max`. */
const editDistance = (a: string, b: string, max: number): number => {
    if (Math.abs(a.length - b.length) > max) {
        return max + 1;
    }
    let prev2: number[] = [];
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const curr = [i];
        let rowMin = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let d = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                d = Math.min(d, prev2[j - 2] + 1);
            }
            curr[j] = d;
            rowMin = Math.min(rowMin, d);
        }
        if (rowMin > max) {
            return max + 1;
        }
        prev2 = prev;
        prev = curr;
    }
    return prev[b.length];
};

interface VocabWord {
    /** Folded form (see foldWord) - what queries are compared against. */
    word: string;
    /** Readable form a suggestion shows (lowercase, umlauts intact). */
    display: string;
    /** Number of occurrences - the tie-breaker: the more common word is the likelier intent. */
    count: number;
}

export class FuzzyVocabulary {
    private readonly words: VocabWord[] = [];
    private readonly byWord = new Map<string, number>();
    private readonly bySpelling = new Map<string, number>();
    private readonly variants = new Map<string, number[]>();
    private built = false;

    /**
     * Registers the words of one item's text. `normalized` is the normalized text, `raw` the
     * original, whose lowercase words become a suggestion's display form. Both split into the same
     * words (normalize only strips diacritics and trims, and splitWords drops the empty edge words
     * that untrimmed raw text would add), which is what lets the display be looked up by position -
     * but only for words not seen before, so the raw text is rarely even split.
     */
    addText(normalized: string, raw: string): void {
        const normWords = splitWords(normalized);
        let rawWords: string[] | undefined;
        for (let i = 0; i < normWords.length; i++) {
            const normWord = normWords[i];
            // Words repeat endlessly across a catalog; the verdict per distinct spelling is cached so
            // that folding and filtering (the costly part) happens once per word, not once per use.
            let id = this.bySpelling.get(normWord);
            if (id === undefined) {
                rawWords ??= splitWords(raw.toLowerCase());
                id = this.register(normWord, rawWords.length === normWords.length ? rawWords[i] : normWord);
                this.bySpelling.set(normWord, id);
            }
            if (id !== SKIPPED) {
                this.words[id].count++;
            }
        }
        this.built = false;
    }

    /** Adds a word if it qualifies for typo matching; returns its id or SKIPPED. */
    private register(normWord: string, display: string): number {
        const word = foldWord(normWord);
        // Digits make for useless suggestions ("58800" -> "58801") - article numbers and sizes are
        // matched exactly by the normal search only.
        if (word.length < MIN_WORD_LENGTH || word.length > MAX_WORD_LENGTH || /\p{N}/u.test(word)) {
            return SKIPPED;
        }
        let id = this.byWord.get(word);
        if (id === undefined) {
            id = this.words.length;
            this.byWord.set(word, id);
            this.words.push({ word, display, count: 0 });
        }
        return id;
    }

    private buildVariants(): void {
        this.variants.clear();
        this.words.forEach(({ word }, id) => {
            const prefix = word.slice(0, PREFIX_LENGTH);
            for (const variant of deletions(prefix, typosAllowed(word.length))) {
                const ids = this.variants.get(variant);
                if (ids) {
                    ids.push(id);
                } else {
                    this.variants.set(variant, [id]);
                }
            }
        });
        this.built = true;
    }

    /** True if `word` is a vocabulary word or part of one - i.e. the normal search would already
     * have found it, so it is no typo. */
    knows(word: string): boolean {
        if (this.byWord.has(word)) {
            return true;
        }
        return this.words.some((w) => w.word.includes(word));
    }

    /** Best vocabulary word within the typo tolerance of `word`, or undefined. */
    correct(word: string): string | undefined {
        if (word.length < MIN_WORD_LENGTH || word.length > MAX_WORD_LENGTH) {
            return undefined;
        }
        if (!this.built) {
            this.buildVariants();
        }
        const queryVariants = deletions(word.slice(0, PREFIX_LENGTH), 2);
        const candidates = new Set<number>();
        for (const variant of queryVariants) {
            this.variants.get(variant)?.forEach((id) => candidates.add(id));
        }
        let best: { id: number; dist: number } | undefined;
        for (const id of candidates) {
            const entry = this.words[id];
            // Tolerance follows the SHORTER of the two words: a long query must not "correct" onto
            // a short word through two typos, nor the other way round.
            const max = typosAllowed(Math.min(word.length, entry.word.length));
            if (max === 0) {
                continue;
            }
            const dist = editDistance(word, entry.word, max);
            if (dist > max) {
                continue;
            }
            if (!best || this.better(id, dist, best, word)) {
                best = { id, dist };
            }
        }
        return best ? this.words[best.id].display : undefined;
    }

    // Closer wins; then a shared first letter (typos rarely hit it); then the more common word.
    private better(id: number, dist: number, best: { id: number; dist: number }, word: string): boolean {
        if (dist !== best.dist) {
            return dist < best.dist;
        }
        const firstA = this.words[id].word[0] === word[0];
        const firstB = this.words[best.id].word[0] === word[0];
        if (firstA !== firstB) {
            return firstA;
        }
        return this.words[id].count > this.words[best.id].count;
    }
}
